import "./newUser.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useEffect, useState } from "react";
import data from "../../../data/data";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  Box,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

const NewUser = ({ inputs, title }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { state } = useLocation();
  const userData = state?.user || {};
  const isEditMode = !!userData._id;

  const [formData, setFormData] = useState({
    district: userData.district || "Myagdi",
    palika: userData.palika || "Beni",
    wardNo: userData.wardNo || "",
    villageName: userData.villageName || "",
    clubName: userData.clubName || "",
    villageNameAlias: userData.villageNameAlias || "",
    clubNameAlias: userData.clubNameAlias || "",
    slogan: userData.slogan || "",
    estDate: userData.estDate || "",
    villageImage: userData.villageImage || "",
    clubIcon: userData.clubIcon || "",
    email: userData.email || "",
    phoneNumber: userData.phoneNumber || "",
    password: "", // Don't populate password for security
    adminType: userData.adminType || "Admin",
    address: userData.address || "",
    latitude: userData.latitude || "28.52037",
    longitude: userData.longitude || "83.6764",
    editedDate: "",
  });

  const [villageImageFile, setVillageImageFile] = useState(null);
  const [clubIconFile, setClubIconFile] = useState(null);
  const [villageImagePreview, setVillageImagePreview] = useState(
    userData.villageImage || "",
  );
  const [clubIconPreview, setClubIconPreview] = useState(
    userData.clubIcon || "",
  );

  const [loading, setLoading] = useState(false);
  const [palikas, setPalikas] = useState([]);
  const [errors, setErrors] = useState({});
  const [backEndError, setBackEndError] = useState("");

  useEffect(() => {
    if (userData.district) {
      const district = data.districts.find((d) => d.name === userData.district);
      setPalikas(district ? district.palikas : []);
    } else {
      const defaultDistrict = data.districts.find((d) => d.name === "Myagdi");
      setPalikas(defaultDistrict ? defaultDistrict.palikas : []);
    }
  }, [userData.district]);

  const handleDistrictChange = (e) => {
    const districtName = e.target.value;
    setFormData({
      ...formData,
      district: districtName,
      palika: "",
    });

    const district = data.districts.find((d) => d.name === districtName);
    setPalikas(district ? district.palikas : []);
  };

  const handlePalikaChange = (e) => {
    const palikaName = e.target.value;
    setFormData({
      ...formData,
      palika: palikaName,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleVillageImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVillageImageFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setVillageImagePreview(previewUrl);
    }
  };

  const handleClubIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setClubIconFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setClubIconPreview(previewUrl);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.district) errors.district = "District is required.";
    if (!formData.palika) errors.palika = "UM/RM (Palika) is required.";
    if (!formData.estDate) errors.estDate = "Established Date is required.";
    if (!formData.villageName) errors.villageName = "Village name is required.";
    if (!formData.villageNameAlias)
      errors.villageNameAlias = "Village Alias name is required.";
    if (!formData.clubName) errors.clubName = "Club name is required.";
    if (!formData.clubNameAlias)
      errors.clubNameAlias = "Club Alias name is required.";
    if (!formData.wardNo) errors.wardNo = "Ward number is required.";
    if (!/^\d+$/.test(formData.wardNo))
      errors.wardNo = "Ward number must be a number.";
    if (!formData.phoneNumber) errors.phoneNumber = "Phone number is required.";
    if (!/^\d{10}$/.test(formData.phoneNumber))
      errors.phoneNumber = "Phone number must be 10 digits.";
    if (!formData.email) errors.email = "Email is required.";
    if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = "Email format is invalid.";

    // Image validation - only required for new users
    if (!isEditMode) {
      if (!villageImageFile) errors.villageImage = "Village image is required.";
      if (!clubIconFile) errors.clubIcon = "Club logo is required.";
    }

    if (!formData.address) errors.address = "Address is required.";
    if (!formData.latitude) errors.latitude = "Latitude is required.";
    if (!formData.longitude) errors.longitude = "Longitude is required.";

    // Password validation - only required for new users
    if (!isEditMode) {
      if (!formData.password) errors.password = "Password is required.";
      if (formData.password && formData.password.length < 8)
        errors.password = "Password must be at least 8 characters long.";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setBackEndError("");

    const formDataToSubmit = new FormData();

    // Append all form fields
    Object.keys(formData).forEach((key) => {
      if (key !== "villageImage" && key !== "clubIcon" && formData[key]) {
        formDataToSubmit.append(key, formData[key]);
      }
    });

    // Append image files only if new files are selected
    if (villageImageFile) {
      formDataToSubmit.append("villageImage", villageImageFile);
    }

    if (clubIconFile) {
      formDataToSubmit.append("clubIcon", clubIconFile);
    }

    const MAIN_URL = isEditMode
      ? `${BASE_URL}/api/users/updateAdminUser/${userData._id}`
      : `${BASE_URL}/api/users/registerAdminUser`;

    try {
      const response = await axios({
        method: isEditMode ? "put" : "post",
        url: MAIN_URL,
        data: formDataToSubmit,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201 || response.status === 200) {
        // Clean up preview URLs to prevent memory leaks
        if (villageImagePreview && villageImagePreview.startsWith("blob:")) {
          URL.revokeObjectURL(villageImagePreview);
        }
        if (clubIconPreview && clubIconPreview.startsWith("blob:")) {
          URL.revokeObjectURL(clubIconPreview);
        }
        navigate("/users");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || "An error occurred";
      setBackEndError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      if (villageImagePreview && villageImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(villageImagePreview);
      }
      if (clubIconPreview && clubIconPreview.startsWith("blob:")) {
        URL.revokeObjectURL(clubIconPreview);
      }
    };
  }, []);

  return (
    <div className="newUser">
      <Sidebar />
      <div className="newUserContainer">
        <Navbar />
        <div className="top">
          <h1>{isEditMode ? "Edit User" : title}</h1>
          {isEditMode && (
            <Chip
              label={`ID: ${userData._id}`}
              size="small"
              color="primary"
              variant="outlined"
              style={{ marginLeft: "1rem" }}
            />
          )}
        </div>
        <div className="bottom">
          <form onSubmit={handleSubmit}>
            <Paper
              elevation={3}
              style={{ padding: "2rem", marginBottom: "2rem" }}
            >
              <Typography variant="h6" gutterBottom>
                <ImageIcon
                  style={{ marginRight: "0.5rem", verticalAlign: "middle" }}
                />
                Village Images
              </Typography>
              <Divider style={{ marginBottom: "1.5rem" }} />

              <Grid container spacing={3}>
                {/* Village Image Upload */}
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <input
                      accept="image/*"
                      id="villageImage"
                      type="file"
                      name="villageImage"
                      onChange={handleVillageImageChange}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="villageImage">
                      <IconButton color="primary" component="span" size="large">
                        <CloudUploadIcon fontSize="large" />
                      </IconButton>
                      <Typography variant="body2" color="textSecondary">
                        Click to upload village image
                      </Typography>
                    </label>

                    {/* Image Preview */}
                    <Box mt={2}>
                      {villageImagePreview ? (
                        <img
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "2px solid #e0e0e0",
                          }}
                          src={villageImagePreview}
                          alt="Village Preview"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg";
                          }}
                        />
                      ) : (
                        <img
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "2px solid #e0e0e0",
                            opacity: 0.5,
                          }}
                          src="https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg"
                          alt="No image"
                        />
                      )}
                    </Box>

                    {villageImageFile && (
                      <Typography
                        variant="caption"
                        display="block"
                        style={{ marginTop: "0.5rem" }}
                      >
                        New file selected: {villageImageFile.name}
                      </Typography>
                    )}

                    {isEditMode &&
                      !villageImageFile &&
                      formData.villageImage && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="primary"
                          style={{ marginTop: "0.5rem" }}
                        >
                          Current image will be preserved
                        </Typography>
                      )}

                    {errors.villageImage && (
                      <span className="error-message">
                        {errors.villageImage}
                      </span>
                    )}
                  </Box>
                </Grid>

                {/* Club Icon Upload */}
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <input
                      accept="image/*"
                      id="clubIcon"
                      type="file"
                      name="clubIcon"
                      onChange={handleClubIconChange}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="clubIcon">
                      <IconButton color="primary" component="span" size="large">
                        <CloudUploadIcon fontSize="large" />
                      </IconButton>
                      <Typography variant="body2" color="textSecondary">
                        Click to upload club logo
                      </Typography>
                    </label>

                    {/* Image Preview */}
                    <Box mt={2}>
                      {clubIconPreview ? (
                        <img
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid #e0e0e0",
                          }}
                          src={clubIconPreview}
                          alt="Club Preview"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg";
                          }}
                        />
                      ) : (
                        <img
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid #e0e0e0",
                            opacity: 0.5,
                          }}
                          src="https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg"
                          alt="No image"
                        />
                      )}
                    </Box>

                    {clubIconFile && (
                      <Typography
                        variant="caption"
                        display="block"
                        style={{ marginTop: "0.5rem" }}
                      >
                        New file selected: {clubIconFile.name}
                      </Typography>
                    )}

                    {isEditMode && !clubIconFile && formData.clubIcon && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="primary"
                        style={{ marginTop: "0.5rem" }}
                      >
                        Current logo will be preserved
                      </Typography>
                    )}

                    {errors.clubIcon && (
                      <span className="error-message">{errors.clubIcon}</span>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} style={{ padding: "2rem" }}>
              <Typography variant="h6" gutterBottom>
                Village Details
              </Typography>
              <Divider style={{ marginBottom: "1.5rem" }} />

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormControl fullWidth error={Boolean(errors.district)}>
                    <InputLabel>District *</InputLabel>
                    <Select
                      name="district"
                      value={formData.district}
                      onChange={handleDistrictChange}
                    >
                      {data.districts.map((district) => (
                        <MenuItem key={district.name} value={district.name}>
                          {district.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.district && (
                      <Typography variant="caption" color="error">
                        {errors.district}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth error={Boolean(errors.palika)}>
                    <InputLabel>UM/RM *</InputLabel>
                    <Select
                      name="palika"
                      value={formData.palika}
                      onChange={handlePalikaChange}
                    >
                      {palikas.map((palika) => (
                        <MenuItem key={palika.name} value={palika.name}>
                          {palika.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.palika && (
                      <Typography variant="caption" color="error">
                        {errors.palika}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="wardNo"
                    label="Ward No *"
                    type="number"
                    value={formData.wardNo}
                    onChange={handleInputChange}
                    error={Boolean(errors.wardNo)}
                    helperText={errors.wardNo}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="villageName"
                    label="Village Name *"
                    value={formData.villageName}
                    onChange={handleInputChange}
                    error={Boolean(errors.villageName)}
                    helperText={errors.villageName}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="clubName"
                    label="Club Name *"
                    value={formData.clubName}
                    onChange={handleInputChange}
                    error={Boolean(errors.clubName)}
                    helperText={errors.clubName}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="villageNameAlias"
                    label="Village Name Alias *"
                    value={formData.villageNameAlias}
                    onChange={handleInputChange}
                    error={Boolean(errors.villageNameAlias)}
                    helperText={errors.villageNameAlias}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="clubNameAlias"
                    label="Club Name Alias *"
                    value={formData.clubNameAlias}
                    onChange={handleInputChange}
                    error={Boolean(errors.clubNameAlias)}
                    helperText={errors.clubNameAlias}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="phoneNumber"
                    label="Phone No *"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    error={Boolean(errors.phoneNumber)}
                    helperText={errors.phoneNumber}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="slogan"
                    label="Slogan *"
                    value={formData.slogan}
                    onChange={handleInputChange}
                    error={Boolean(errors.slogan)}
                    helperText={errors.slogan}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="estDate"
                    label="Established Date *"
                    value={formData.estDate}
                    onChange={handleInputChange}
                    error={Boolean(errors.estDate)}
                    helperText={errors.estDate}
                    required
                    placeholder="YYYY-MM-DD"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="password"
                    label={
                      isEditMode
                        ? "Password (leave blank to keep current)"
                        : "Password *"
                    }
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                    required={!isEditMode}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="address"
                    label="Address *"
                    value={formData.address}
                    onChange={handleInputChange}
                    error={Boolean(errors.address)}
                    helperText={errors.address}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="adminType"
                    label="Admin Type"
                    value={formData.adminType}
                    disabled
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="latitude"
                    label="Latitude *"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    error={Boolean(errors.latitude)}
                    helperText={errors.latitude}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="longitude"
                    label="Longitude *"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    error={Boolean(errors.longitude)}
                    helperText={errors.longitude}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            {backEndError && (
              <Typography color="error" style={{ marginTop: "1rem" }}>
                {backEndError}
              </Typography>
            )}

            <Box mt={3} display="flex" justifyContent="flex-start">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                style={{ minWidth: "120px" }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate("/users")}
                style={{ marginLeft: "1rem" }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewUser;
